import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Lock, ArrowLeft } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { useToast } from '../../hooks/use-toast';
import api from '../../lib/api';

interface AcceptInvitationFormData {
  password: string;
  confirmPassword: string;
}

export function AcceptInvitationForm() {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { register, handleSubmit, formState: { errors }, watch } = useForm<AcceptInvitationFormData>();

  const token = searchParams.get('token');
  const password = watch('password');

  useEffect(() => {
    if (!token) {
      toast({
        title: "Invalid Invitation Link",
        description: "The invitation link is invalid or has expired.",
        variant: "destructive",
      });
      navigate('/login');
    }
  }, [token, navigate, toast]);

  const onSubmit = async (data: AcceptInvitationFormData) => {
    if (!token) return;

    setIsSubmitting(true);
    try {
      await api.acceptInvitation({
        token,
        password: data.password,
      });

      toast({
        title: "Invitation Accepted",
        description: "Your account has been set up successfully. You can now log in.",
      });
      
      navigate('/login');
    } catch (error) {
      toast({
        title: "Invitation Failed",
        description: error instanceof Error ? error.message : "An error occurred while accepting the invitation",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const passwordRequirements = [
    { regex: /.{8,}/, text: "At least 8 characters" },
    { regex: /[A-Z]/, text: "One uppercase letter" },
    { regex: /[a-z]/, text: "One lowercase letter" },
    { regex: /\d/, text: "One number" },
    { regex: /[!@#$%^&*]/, text: "One special character" },
  ];

  if (!token) {
    return null; // Will redirect in useEffect
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">ISMS Manager</h1>
          <p className="text-gray-600">Information Security Management System</p>
          <p className="text-sm text-gray-500 mt-1">NIS2 Compliance Tool</p>
        </div>

        <Card className="w-full max-w-md mx-auto">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-bold text-center">Accept Invitation</CardTitle>
            <CardDescription className="text-center">
              Set up your password to complete account activation
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password">Create Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Create your password"
                    className="pl-10 pr-10"
                    {...register('password', {
                      required: 'Password is required',
                      validate: (value) => {
                        const requirements = [
                          { test: /.{8,}/, message: "At least 8 characters" },
                          { test: /[A-Z]/, message: "One uppercase letter" },
                          { test: /[a-z]/, message: "One lowercase letter" },
                          { test: /\d/, message: "One number" },
                          { test: /[!@#$%^&*]/, message: "One special character" },
                        ];
                        
                        for (const req of requirements) {
                          if (!req.test.test(value)) {
                            return req.message;
                          }
                        }
                        return true;
                      }
                    })}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <Eye className="h-4 w-4 text-muted-foreground" />
                    )}
                  </Button>
                </div>
                
                {/* Password requirements */}
                <div className="text-xs space-y-1">
                  {passwordRequirements.map((req, index) => (
                    <div
                      key={index}
                      className={`flex items-center space-x-2 ${
                        password && req.regex.test(password)
                          ? 'text-green-600'
                          : 'text-muted-foreground'
                      }`}
                    >
                      <span>{password && req.regex.test(password) ? '✓' : '○'}</span>
                      <span>{req.text}</span>
                    </div>
                  ))}
                </div>
                
                {errors.password && (
                  <p className="text-sm text-destructive">{errors.password.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="Confirm your password"
                    className="pl-10 pr-10"
                    {...register('confirmPassword', {
                      required: 'Please confirm your password',
                      validate: (value) =>
                        value === password || 'Passwords do not match'
                    })}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <Eye className="h-4 w-4 text-muted-foreground" />
                    )}
                  </Button>
                </div>
                {errors.confirmPassword && (
                  <p className="text-sm text-destructive">{errors.confirmPassword.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Button 
                  type="submit" 
                  className="w-full" 
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Setting up Account..." : "Complete Setup"}
                </Button>

                <Button
                  type="button"
                  variant="ghost"
                  className="w-full"
                  onClick={() => navigate('/login')}
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Login
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center mt-8 text-xs text-gray-500">
          <p>&copy; 2025 ISMS Manager. All rights reserved.</p>
          <p className="mt-1">Built for NIS2 directive compliance</p>
        </div>
      </div>
    </div>
  );
}
